import { LockIcon } from '@/components/atoms/icons';
import { ContentDialog } from '@/components/molecules/dialogs/ContentDialog';
import { DT_SCREEN } from '@/core/constants/dynatraceScreens';
import { useUIColors } from '@/core/hooks';
import {
  trackCatalogEvent,
  trackGuestLoginPromptMaybeLaterClick,
} from '@/core/services/trackDynatraceCatalog';
import React, { useEffect, useMemo, useRef } from 'react';
import { StyleSheet, useWindowDimensions, View } from 'react-native';
import {
  getGuestDetailsLayout,
  getGuestDetailsTypeScale,
} from '../constants/guestLayout';
import { useGuestLabels } from '../hooks/useGuestLabels';

export interface GuestLoginPromptDialogProps {
  readonly visible: boolean;
  readonly onMaybeLater: () => void;
  readonly onLogin: () => void;
}

/**
 * Guest login prompt — ContentDialog shell.
 * Desktop: Figma 513×290. Compact: content height, smaller type/icon/padding.
 */
export function GuestLoginPromptDialog({
  visible,
  onMaybeLater,
  onLogin,
}: Readonly<GuestLoginPromptDialogProps>) {
  const colors = useUIColors();
  const labels = useGuestLabels();
  const { width } = useWindowDimensions();
  const layout = useMemo(() => getGuestDetailsLayout(width), [width]);
  const typeScale = useMemo(() => getGuestDetailsTypeScale(layout), [layout]);
  const pad = typeScale.loginDialogPadding;
  const viewTrackedRef = useRef(false);

  useEffect(() => {
    if (!visible) {
      viewTrackedRef.current = false;
      return;
    }
    if (viewTrackedRef.current) {
      return;
    }
    viewTrackedRef.current = true;
    trackCatalogEvent({
      screen: DT_SCREEN.GUEST,
      actionName: 'guest_login_prompt_view',
      eventType: 'view',
    });
  }, [visible]);

  const handleLoginPress = () => {
    trackCatalogEvent({
      screen: DT_SCREEN.GUEST,
      actionName: 'guest_login_prompt_login_click',
      eventType: 'action',
    });
    onLogin();
  };

  const handleMaybeLaterPress = () => {
    trackGuestLoginPromptMaybeLaterClick();
    onMaybeLater();
  };

  return (
    <ContentDialog
      visible={visible}
      onClose={onMaybeLater}
      contentAlign="center"
      maxWidth={513}
      cardStyle={[
        styles.card,
        {
          paddingTop: pad,
          paddingBottom: pad,
          paddingLeft: pad,
          paddingRight: pad,
        },
        layout.isCompact ? styles.cardCompact : styles.cardDesktop,
      ]}
      headerStyle={[
        styles.header,
        { gap: layout.isPhone ? 6 : 8 },
      ]}
      actionsStyle={[styles.actions, { marginTop: typeScale.loginDialogGap }]}
      icon={
        <View
          style={[
            styles.iconWrap,
            { marginBottom: layout.isPhone ? 8 : 16 },
          ]}
        >
          <LockIcon
            size={typeScale.loginDialogIconSize}
            color={colors.fontPrimary}
          />
        </View>
      }
      title={labels.loginPromptTitle}
      titleVariant={typeScale.loginDialogTitle}
      titleColor="primary"
      titleStyle={[
        styles.title,
        { lineHeight: typeScale.loginDialogTitleLineHeight },
      ]}
      subtitle={labels.loginPromptMessage}
      subtitleVariant={typeScale.loginDialogSubtitle}
      subtitleColor="secondary"
      subtitleStyle={[
        styles.subtitle,
        { marginTop: layout.isPhone ? 4 : 8 },
      ]}
      actions={[
        {
          label: labels.maybeLater,
          variant: 'outline',
          flex: true,
          onPress: handleMaybeLaterPress,
          style: { backgroundColor: colors.backgroundLevel1 },
          textStyle: [
            styles.actionLabel,
            {
              fontSize: typeScale.loginActionFontSize,
              lineHeight: typeScale.loginActionLineHeight,
            },
          ],
        },
        {
          label: labels.login,
          variant: 'primary',
          flex: true,
          onPress: handleLoginPress,
          textStyle: [
            styles.actionLabel,
            {
              fontSize: typeScale.loginActionFontSize,
              lineHeight: typeScale.loginActionLineHeight,
            },
          ],
        },
      ]}
    />
  );
}

const styles = StyleSheet.create({
  card: {
    width: '100%',
    maxWidth: 513,
    borderRadius: 16,
    gap: 0,
    justifyContent: 'space-between',
  },
  cardDesktop: {
    height: 290,
  },
  /** Hug content on compact — fixed 290 overflows small viewports. */
  cardCompact: {
    height: undefined,
    minHeight: 0,
  },
  header: {
    alignItems: 'center',
    width: '100%',
  },
  iconWrap: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  title: {
    letterSpacing: 0,
    textAlign: 'center',
  },
  subtitle: {
    letterSpacing: 0,
    lineHeight: 16,
    textAlign: 'center',
    width: '100%',
  },
  actions: {
    width: '100%',
    gap: 12,
  },
  actionLabel: {
    fontFamily: 'Matter-SemiBold',
    letterSpacing: 0,
    textAlign: 'center',
  },
});

export default GuestLoginPromptDialog;
